import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Edit, Mail, RefreshCw, Trash2, Users as UsersIcon, AlertTriangle } from "lucide-react";
import Layout from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Usuarios({ user, onLogout }) {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [rolFilter, setRolFilter] = useState("todos");
  const [estadoFilter, setEstadoFilter] = useState("todos");
  
  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    password: "",
    rol: "admin",
    enviar_invitacion: false,
    permisos: []
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // Permissions
  const [availablePermissions, setAvailablePermissions] = useState([]);
  
  // Password reset modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [tempPassword, setTempPassword] = useState("");
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchUsuarios();
    fetchPermissions();
  }, [rolFilter, estadoFilter]);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailablePermissions(response.data.permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  const fetchUsuarios = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/users?search=${searchTerm}&rol=${rolFilter}&estado=${estadoFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsuarios(response.data);
    } catch (error) {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchUsuarios();
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      nombre: "",
      email: "",
      telefono: "",
      password: "",
      rol: "admin",
      enviar_invitacion: false
    });
    setShowModal(true);
  };

  const openEditModal = (usuario) => {
    setEditingUser(usuario);
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      telefono: usuario.telefono || "",
      rol: usuario.rol,
      password: "",
      enviar_invitacion: false,
      permisos: usuario.permisos || []
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.nombre || !formData.email) {
      toast.error("Nombre y email son obligatorios");
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error("La contraseña es obligatoria para nuevos usuarios");
      return;
    }

    setFormLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      if (editingUser) {
        // Update
        const updateData = {
          nombre: formData.nombre,
          telefono: formData.telefono || null,
          rol: formData.rol
        };
        
        // Only include permisos for admin role
        if (formData.rol === "admin") {
          updateData.permisos = formData.permisos;
        }
        
        await axios.put(
          `${API}/users/${editingUser.id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Usuario actualizado exitosamente");
      } else {
        // Create
        await axios.post(
          `${API}/users`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Usuario creado exitosamente");
      }
      
      setShowModal(false);
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar usuario");
    } finally {
      setFormLoading(false);
    }
  };

  const handleResetPassword = async (usuario) => {
    setResetUser(usuario);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/users/${usuario.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTempPassword(response.data.temp_password);
      setShowResetModal(true);
    } catch (error) {
      toast.error("Error al reiniciar contraseña");
    }
  };

  const openDeleteModal = (usuario) => {
    setUserToDelete(usuario);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/users/${userToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Usuario ${userToDelete.nombre} eliminado exitosamente`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSendInvitation = async (usuario) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/users/${usuario.id}/send-invitation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Invitación enviada exitosamente");
    } catch (error) {
      toast.error("Error al enviar invitación");
    }
  };

  const getRolLabel = (rol) => {
    return rol === "maestro" ? "Maestro" : rol === "admin" ? "Administrador" : "Propietario";
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-7xl mx-auto space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestión de Usuarios</h1>
            <p className="text-gray-500 mt-1">Administra los usuarios del sistema</p>
          </div>
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Buscar por nombre, email o teléfono..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={rolFilter} onValueChange={setRolFilter}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los roles</SelectItem>
                  <SelectItem value="maestro">Maestro</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="propietario">Propietario</SelectItem>
                </SelectContent>
              </Select>

              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="activo">Activo</SelectItem>
                  <SelectItem value="inactivo">Inactivo</SelectItem>
                </SelectContent>
              </Select>
              
              <Button onClick={handleSearch}>
                <Search className="w-4 h-4 mr-2" />
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Cargando...</div>
            ) : usuarios.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No hay usuarios para mostrar</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usuarios.map((usuario) => (
                      <tr key={usuario.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{usuario.nombre}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{usuario.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">{usuario.telefono || "-"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="badge badge-activo">{getRolLabel(usuario.rol)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`badge ${usuario.estado === "activo" ? "badge-activo" : "badge-vencido"}`}>
                            {usuario.estado === "activo" ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEditModal(usuario)} title="Editar">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleResetPassword(usuario)} title="Reiniciar contraseña">
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleSendInvitation(usuario)} title="Enviar invitación">
                              <Mail className="w-4 h-4" />
                            </Button>
                            {usuario.rol !== "maestro" && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => openDeleteModal(usuario)}
                                title="Eliminar usuario"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? "Editar Usuario" : "Nuevo Usuario"}</DialogTitle>
            <DialogDescription>
              {editingUser ? "Modifica los datos del usuario" : "Crea un nuevo usuario en el sistema"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Juan Pérez"
              />
            </div>

            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="usuario@ejemplo.com"
                disabled={!!editingUser}
              />
            </div>

            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="5551234567"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label>Contraseña *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Rol *</Label>
              <Select 
                value={formData.rol} 
                onValueChange={(value) => setFormData({ ...formData, rol: value, permisos: value === "admin" ? formData.permisos : [] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="propietario">Propietario</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.rol === "admin" && (
              <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Permisos de Administrador</Label>
                  <span className="text-sm text-gray-500">
                    {formData.permisos.length} de {availablePermissions.length} seleccionados
                  </span>
                </div>
                <p className="text-xs text-gray-600">
                  Selecciona los permisos que tendrá este administrador
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availablePermissions.map((permission) => (
                    <div key={permission.key} className="flex items-start space-x-3 p-2 hover:bg-white rounded transition-colors">
                      <input
                        type="checkbox"
                        id={permission.key}
                        checked={formData.permisos.includes(permission.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, permisos: [...formData.permisos, permission.key] });
                          } else {
                            setFormData({ ...formData, permisos: formData.permisos.filter(p => p !== permission.key) });
                          }
                        }}
                        className="mt-1 rounded"
                      />
                      <div className="flex-1">
                        <Label htmlFor={permission.key} className="cursor-pointer font-medium">
                          {permission.label}
                        </Label>
                        <p className="text-xs text-gray-500">{permission.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({ ...formData, permisos: availablePermissions.map(p => p.key) })}
                  >
                    Seleccionar Todos
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({ ...formData, permisos: [] })}
                  >
                    Limpiar Todos
                  </Button>
                </div>
              </div>
            )}

            {!editingUser && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="enviar_invitacion"
                  checked={formData.enviar_invitacion}
                  onChange={(e) => setFormData({ ...formData, enviar_invitacion: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="enviar_invitacion">Enviar invitación por correo</Label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={formLoading}>
              {formLoading ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmar Eliminación
            </DialogTitle>
            <DialogDescription>
              Esta acción es permanente y no se puede deshacer
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-800 mb-2">
                <strong>⚠️ ADVERTENCIA:</strong>
              </p>
              <p className="text-sm text-red-700">
                Estás a punto de eliminar permanentemente al usuario:
              </p>
              {userToDelete && (
                <div className="mt-3 p-3 bg-white rounded border border-red-200">
                  <p className="font-semibold">{userToDelete.nombre}</p>
                  <p className="text-sm text-gray-600">{userToDelete.email}</p>
                  <p className="text-sm text-gray-600">Rol: {userToDelete.rol === "admin" ? "Administrador" : "Propietario"}</p>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <p className="text-sm font-semibold">Consecuencias de eliminar este usuario:</p>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>El usuario ya no podrá iniciar sesión</li>
                <li>Se eliminarán todos sus datos de acceso</li>
                <li>Para que vuelva a tener acceso, deberás crearlo desde cero</li>
                <li>Esta acción es <strong className="text-red-600">PERMANENTE</strong></li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteModal(false);
                setUserToDelete(null);
              }}
              disabled={deleteLoading}
            >
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoading ? "Eliminando..." : "Sí, Eliminar Usuario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contraseña Reiniciada</DialogTitle>
            <DialogDescription>
              La contraseña ha sido reiniciada exitosamente
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800 mb-2">
                <strong>Contraseña temporal:</strong>
              </p>
              <div className="flex items-center justify-between bg-white p-3 rounded border">
                <code className="text-lg font-mono">{tempPassword}</code>
                <Button 
                  size="sm" 
                  onClick={() => {
                    navigator.clipboard.writeText(tempPassword);
                    toast.success("Contraseña copiada");
                  }}
                >
                  Copiar
                </Button>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              El usuario deberá cambiar esta contraseña en su primer inicio de sesión.
            </p>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowResetModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
